/**
 * 广告可见菜单由 Admin → 菜单管理 → 客户端 下发（动态路由）。
 * 当前已配：
 * - /adv/sp → advertising/sponsoredProducts/index（SP广告）
 * - /adv/sb → advertising/sponsoredBrands/index（SB广告）
 * - /adv/sd → advertising/sponsoredDisplay/index（SD广告）
 * - /adv/spend → advertising/adSpend/index（广告花费）
 * - /adv/invoice → advertising/adInvoice/index（广告发票）
 * - /adv/portfolio → advertising/portfolio/index（广告组合，菜单 routeName=AdvPortfolio）
 * - /adv/portfolio/detail → advertising/portfolio/detail（隐藏菜单 routeName=AdvPortfolioDetail）
 *
 * 下方 ADV_STATIC_HIDDEN_ROUTES 是创建/详情等隐藏页的临时静态注册。
 * 由 customer/router/index.ts 交给 createDynamicRouter 的 guaranteedLayoutPages，
 * 登录后挂到唯一 AppLayout 下（不另套一层 Layout，避免 keep-alive 被拆掉）。
 * 菜单已下发同一 path / routeName 时以菜单为准，这里跳过。
 * routeName 须与对应页面 defineOptions.name 一致，AppMain keep-alive 才按页签缓存。
 * TODO: 创建/详情改为 Admin 隐藏菜单 + 角色权限动态下发，去掉这份静态页。
 */
import type { RouteComponent, RouteRecordRaw } from "vue-router";
import { t } from "@shared/utils/i18n";

const advertisingViews = import.meta.glob("../views/advertising/**/*.vue");

export type AdvMenuSeedItem = {
    permissionName: string;
    route: string;
    routeName: string;
    component?: string;
    redirect?: string;
    hidden?: number;
    sort?: number;
    icon?: string;
    selectIcon?: string;
};

/** 创建 / 详情等隐藏页：登录后并入 AppLayout */
export const ADV_STATIC_HIDDEN_ROUTES: AdvMenuSeedItem[] = [
    { permissionName: "SP广告活动详情", route: "/adv/sp/campaign/detail", routeName: "SpCampaignDetail", component: "advertising/sponsoredProducts/details/CampaignDetail", hidden: 2, sort: 10 },
    { permissionName: "SP广告组详情", route: "/adv/sp/adGroup/detail", routeName: "SpAdGroupDetail", component: "advertising/sponsoredProducts/details/AdGroupDetail", hidden: 2, sort: 11 },
    { permissionName: "创建SP广告活动", route: "/adv/sp/campaign/create", routeName: "SpCampaignCreate", component: "advertising/sponsoredProducts/creation/CampaignCreate", hidden: 2, sort: 12 },
    { permissionName: "创建SP广告组", route: "/adv/sp/adGroup/create", routeName: "SpAdGroupCreate", component: "advertising/sponsoredProducts/creation/AdGroupCreate", hidden: 2, sort: 13 },
    { permissionName: "添加SP商品", route: "/adv/sp/productAd/add", routeName: "SpProductAdAdd", component: "advertising/sponsoredProducts/creation/AddProductAd", hidden: 2, sort: 14 },
    { permissionName: "添加SP关键词", route: "/adv/sp/keyword/add", routeName: "SpKeywordAdd", component: "advertising/sponsoredProducts/creation/AddKeyword", hidden: 2, sort: 15 },
    { permissionName: "添加SP商品投放", route: "/adv/sp/targeting/add", routeName: "SpTargetingAdd", component: "advertising/sponsoredProducts/creation/AddProductTargeting", hidden: 2, sort: 16 },
    {
        permissionName: "添加SP否定词",
        route: "/adv/sp/negativeKeyword/add",
        routeName: "SpNegativeKeywordAdd",
        component: "advertising/sponsoredProducts/creation/AddNegativeKeyword",
        hidden: 2,
        sort: 17,
    },
    {
        permissionName: "添加SP否定投放",
        route: "/adv/sp/negativeTarget/add",
        routeName: "SpNegativeTargetAdd",
        component: "advertising/sponsoredProducts/creation/AddNegativeTarget",
        hidden: 2,
        sort: 18,
    },
    { permissionName: "SB广告活动详情", route: "/adv/sb/campaign/detail", routeName: "SbCampaignDetail", component: "advertising/sponsoredBrands/details/CampaignDetail", hidden: 2, sort: 20 },
    { permissionName: "SB广告组详情", route: "/adv/sb/adGroup/detail", routeName: "SbAdGroupDetail", component: "advertising/sponsoredBrands/details/AdGroupDetail", hidden: 2, sort: 21 },
    { permissionName: "创建SB广告活动", route: "/adv/sb/campaign/create", routeName: "SbCampaignCreate", component: "advertising/sponsoredBrands/creation/CampaignCreate", hidden: 2, sort: 22 },
    { permissionName: "创建SB广告组", route: "/adv/sb/adGroup/create", routeName: "SbAdGroupCreate", component: "advertising/sponsoredBrands/creation/AdGroupCreate", hidden: 2, sort: 23 },
    { permissionName: "创建SB广告", route: "/adv/sb/ad/create", routeName: "SbAdCreate", component: "advertising/sponsoredBrands/creation/AdCreate", hidden: 2, sort: 24 },
    { permissionName: "添加SB关键词", route: "/adv/sb/keyword/add", routeName: "SbKeywordAdd", component: "advertising/sponsoredBrands/creation/AddKeyword", hidden: 2, sort: 25 },
    { permissionName: "添加SB商品投放", route: "/adv/sb/targeting/add", routeName: "SbTargetingAdd", component: "advertising/sponsoredBrands/creation/AddTargeting", hidden: 2, sort: 26 },
    {
        permissionName: "添加SB否定词",
        route: "/adv/sb/negativeKeyword/add",
        routeName: "SbNegativeKeywordAdd",
        component: "advertising/sponsoredBrands/creation/AddNegativeKeyword",
        hidden: 2,
        sort: 27,
    },
    {
        permissionName: "添加SB否定投放",
        route: "/adv/sb/negativeTarget/add",
        routeName: "SbNegativeTargetAdd",
        component: "advertising/sponsoredBrands/creation/AddNegativeTarget",
        hidden: 2,
        sort: 28,
    },
    { permissionName: "SD广告活动详情", route: "/adv/sd/campaign/detail", routeName: "SdCampaignDetail", component: "advertising/sponsoredDisplay/details/CampaignDetail", hidden: 2, sort: 30 },
    { permissionName: "SD广告组详情", route: "/adv/sd/adGroup/detail", routeName: "SdAdGroupDetail", component: "advertising/sponsoredDisplay/details/AdGroupDetail", hidden: 2, sort: 31 },
    { permissionName: "创建SD广告活动", route: "/adv/sd/campaign/create", routeName: "SdCampaignCreate", component: "advertising/sponsoredDisplay/creation/CampaignCreate", hidden: 2, sort: 32 },
    { permissionName: "创建SD广告组", route: "/adv/sd/adGroup/create", routeName: "SdAdGroupCreate", component: "advertising/sponsoredDisplay/creation/AdGroupCreate", hidden: 2, sort: 33 },
    { permissionName: "添加SD广告", route: "/adv/sd/ad/add", routeName: "SdAdAdd", component: "advertising/sponsoredDisplay/creation/AddAd", hidden: 2, sort: 34 },
    { permissionName: "添加SD商品投放", route: "/adv/sd/targeting/add", routeName: "SdTargetingAdd", component: "advertising/sponsoredDisplay/creation/AddProductTargeting", hidden: 2, sort: 35 },
    { permissionName: "添加SD受众", route: "/adv/sd/audience/add", routeName: "SdAudienceAdd", component: "advertising/sponsoredDisplay/creation/AddAudience", hidden: 2, sort: 36 },
    {
        permissionName: "添加SD否定投放",
        route: "/adv/sd/negativeTarget/add",
        routeName: "SdNegativeTargetAdd",
        component: "advertising/sponsoredDisplay/creation/AddNegativeTarget",
        hidden: 2,
        sort: 37,
    },
    { permissionName: "编辑SD广告创意", route: "/adv/sd/ad/creative/edit", routeName: "SdAdCreativeEdit", component: "advertising/sponsoredDisplay/creation/EditAdCreative", hidden: 2, sort: 38 },
];

function resolveAdvView(component: string): RouteComponent | null {
    const key = `../views/${component}.vue`;
    const loader = advertisingViews[key];
    if (!loader) {
        console.warn(`[advRoutes] 未找到页面: ${key}`);
        return null;
    }
    return loader as RouteComponent;
}

/** 创建 / 详情隐藏页：path 为绝对地址，挂到 AppLayout 时再改成相对 path */
export const advGuaranteedLayoutPages: RouteRecordRaw[] = ADV_STATIC_HIDDEN_ROUTES.flatMap(item => {
    if (!item.component || !item.route || !item.routeName) return [];
    const component = resolveAdvView(item.component);
    if (!component) return [];
    return [
        {
            path: item.route,
            name: item.routeName,
            component,
            meta: {
                title: t(item.permissionName),
                hideInMenu: true,
            },
        },
    ];
});
